/* Share-Clinq button.
 * - Uses Web Share API on supported browsers (Android, iOS Safari → native share sheet)
 * - Falls back to copy-to-clipboard + toast on desktop browsers without share support
 * - Variant: 'dark' for the CTA banner / dark hero; 'light' anywhere else */
import { useState, useCallback } from "react";
import { Share2, Check, Copy } from "lucide-react";
import { PUBLIC_APP_URL } from "../../lib/api";

const SHARE_DATA = {
  title: "Clinq — Connecting Funder-Ready SMEs to Capital",
  text: "Find out if your business is Funder-Ready. Get your Capital Readiness Score in 5 minutes and match with Angel Investors, Local Funders, Venture Capital, Private Equity, Banks and Development Finance Institutions across Africa.",
  url: PUBLIC_APP_URL,
};

export default function ShareClinqButton({ variant = "dark", testid = "share-clinq-btn", className = "" }) {
  const [status, setStatus] = useState("idle"); // 'idle' | 'shared' | 'copied'

  const handleShare = useCallback(async () => {
    const baseData = { ...SHARE_DATA, url: PUBLIC_APP_URL };

    // Try sharing WITH the OG image attached so the recipient app
    // (WhatsApp / iMessage etc.) shows the picture inline.
    try {
      const res = await fetch("/og-share.jpg", { cache: "force-cache" });
      if (res.ok) {
        const blob = await res.blob();
        const file = new File([blob], "clinq-share.jpg", { type: "image/jpeg" });
        const dataWithFile = { ...baseData, files: [file] };
        if (navigator.canShare?.(dataWithFile)) {
          await navigator.share(dataWithFile);
          setStatus("shared");
          return;
        }
      }
    } catch (e) {
      // Native file-share unsupported / blocked — fall through to text-only share.
      if (process.env.NODE_ENV !== "production") console.debug("[share] file share fallback:", e?.message);
    }

    // Fallback: text-only Web Share
    if (navigator.share && navigator.canShare?.(baseData)) {
      try {
        await navigator.share(baseData);
        setStatus("shared");
      } catch (e) {
        // User dismissed the native share sheet — not an error.
        if (process.env.NODE_ENV !== "production") console.debug("[share] dismissed by user:", e?.message);
      }
      return;
    }
    // Last resort: copy URL to clipboard
    try {
      await navigator.clipboard.writeText(baseData.url);
      setStatus("copied");
      setTimeout(() => setStatus("idle"), 2200);
    } catch {
      window.prompt("Copy the Clinq link:", baseData.url);
    }
  }, []);

  const baseDark =
    "inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/20";
  const baseLight =
    "inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-800 shadow-sm transition-all hover:border-[#0A8B85] hover:text-[#0A8B85]";

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid={testid}
      aria-label="Share Clinq"
      className={(variant === "dark" ? baseDark : baseLight) + " " + className}
    >
      {status === "copied" ? (
        <><Check className="h-4 w-4 text-[#7BD3CE]" /> Link copied</>
      ) : status === "shared" ? (
        <><Check className="h-4 w-4 text-[#7BD3CE]" /> Shared</>
      ) : (
        <>
          {variant === "dark" ? <Share2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          Share Clinq
        </>
      )}
    </button>
  );
}
